import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import DangerZoneTab from '../components/DangerZoneTab';

export default function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('account');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
        </div>
      </div>

      <div className="panel-tabs" style={{ marginBottom: 20, padding: 0 }}>
        <button className={'panel-tab' + (tab === 'account' ? ' active' : '')} onClick={() => setTab('account')}>My Account</button>
        {user?.role === 'admin' && (
          <button className={'panel-tab' + (tab === 'users' ? ' active' : '')} onClick={() => setTab('users')}>Users</button>
        )}
        {user?.role === 'admin' && (
          <button className={'panel-tab' + (tab === 'data' ? ' active' : '')} onClick={() => setTab('data')}>Data Management</button>
        )}
        {user?.role === 'admin' && (
          <button className={'panel-tab' + (tab === 'danger' ? ' active' : '')} onClick={() => setTab('danger')} style={{ color: tab === 'danger' ? 'var(--danger-text)' : undefined }}>Danger Zone</button>
        )}
      </div>

      {tab === 'account' && <AccountTab />}
      {tab === 'users' && user?.role === 'admin' && <UsersTab />}
      {tab === 'data' && user?.role === 'admin' && <DataManagementTab />}
      {tab === 'danger' && user?.role === 'admin' && <DangerZoneTab />}
    </div>
  );
}

function AccountTab() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit() {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.put('/auth/password', form);
      setMessage('Password updated');
      setForm({ current_password: '', new_password: '' });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update password');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 480 }}>
      <div style={{ marginBottom: 24 }}>
        <div className="form-hint">Name</div>
        <div style={{ fontSize: 16 }}>{user?.name}</div>
        <div className="form-hint" style={{ marginTop: 12 }}>Email</div>
        <div style={{ fontSize: 16 }}>{user?.email}</div>
        <div className="form-hint" style={{ marginTop: 12 }}>Role</div>
        <Badge>{user?.role}</Badge>
      </div>

      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Change password</h4>
      {message && <div style={{ background: 'var(--success-bg)', color: 'var(--success-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>{message}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>{error}</div>}
      <div className="form-field" style={{ marginBottom: 14 }}>
        <label>Current password</label>
        <input type="password" value={form.current_password} onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))} />
      </div>
      <div className="form-field" style={{ marginBottom: 14 }}>
        <label>New password</label>
        <input type="password" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} />
      </div>
      <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Update Password'}</button>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const [people, setPeople] = useState([]);
  const [editingUser, setEditingUser] = useState(null); // null = closed, 'new' = add, {...} = editing existing
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'user', person_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function load() {
    api.get('/auth/users').then(r => setUsers(r.data));
    api.get('/people').then(r => setPeople(r.data));
  }
  useEffect(load, []);

  function openAdd() {
    setForm({ name: '', email: '', password: '', role: 'user', person_id: '' });
    setError('');
    setEditingUser('new');
  }

  function openEdit(u) {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, person_id: u.person_id || '' });
    setError('');
    setEditingUser(u);
  }

  async function handleSubmit() {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        person_id: form.person_id ? Number(form.person_id) : null,
      };
      if (form.password) payload.password = form.password;

      if (editingUser === 'new') {
        if (!form.password) { setError('Password is required for a new user'); setSaving(false); return; }
        await api.post('/auth/users', payload);
      } else {
        await api.put(`/auth/users/${editingUser.id}`, payload);
      }
      setEditingUser(null);
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save user');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u) {
    await api.put(`/auth/users/${u.id}`, { is_active: u.is_active ? 0 : 1 });
    load();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
      </div>

      {editingUser !== null && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 20 }}>
          {error && <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>{error}</div>}
          <div className="form-grid">
            <div className="form-field"><label>Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="form-field"><label>Email</label><input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></div>
            <div className="form-field">
              <label>Password {editingUser !== 'new' && <span className="form-hint">(leave blank to keep unchanged)</span>}</label>
              <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div className="form-field">
              <label>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-field span-2">
              <label>Linked Person</label>
              <select value={form.person_id} onChange={e => setForm(f => ({ ...f, person_id: e.target.value }))}>
                <option value="">No linked Person</option>
                {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.role ? ` (${p.role})` : ''}</option>)}
              </select>
              <span className="form-hint">Used to default the Owner field when this user creates a Work Order. Without a linked Person, they can't create Work Orders.</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <button className="btn btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : editingUser === 'new' ? 'Add User' : 'Save changes'}</button>
          </div>
        </div>
      )}

      {users === null && <StateBlock type="loading" />}
      {users && (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Linked Person</th><th>Status</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>{u.name}</td>
                <td>{u.email}</td>
                <td><Badge>{u.role}</Badge></td>
                <td>{u.person_name || <span className="form-hint">None</span>}</td>
                <td><Badge variant={u.is_active ? 'success' : 'neutral'}>{u.is_active ? 'Active' : 'Inactive'}</Badge></td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn btn-secondary" onClick={() => openEdit(u)} style={{ marginRight: 8 }}>Edit</button>
                  <button className="btn btn-secondary" onClick={() => toggleActive(u)}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const TABLE_LABELS = {
  clients: 'Clients', vendors: 'Vendors', contractors: 'Contractors', people: 'People',
  work_orders: 'Work Orders', purchase_orders: 'Purchase Orders', timesheets: 'Timesheets',
  ar_invoices: 'AR Invoices', ap_invoices: 'AP Invoices', ar_payments: 'AR Payments',
  ap_payments: 'AP Payments', bank_transactions: 'Bank Transactions',
  business_expenses: 'Business Expenses', person_payments: 'Person Payments',
  person_expenses: 'Person Expenses', entity_contacts: 'Contacts', person_wo_link: 'Person-WO Links',
  person_commissions: 'Commission Rates', documents: 'Documents',
};

function DataManagementTab() {
  const [counts, setCounts] = useState(null);
  const [deleteForm, setDeleteForm] = useState({ table: '', id: '' });
  const [deleteResult, setDeleteResult] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [wiping, setWiping] = useState(false);
  const [wipeResult, setWipeResult] = useState('');

  function load() { api.get('/data/counts').then(r => setCounts(r.data)); }
  useEffect(load, []);

  async function handleDelete() {
    if (!deleteForm.table || !deleteForm.id) return;
    setDeleting(true);
    setDeleteResult('');
    try {
      const r = await api.delete(`/data/${deleteForm.table}/${deleteForm.id}`);
      setDeleteResult(r.data.message);
      setDeleteForm({ table: '', id: '' });
      load();
    } catch (e) {
      setDeleteResult(e.response?.data?.error || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  }

  async function handleWipe() {
    setWiping(true);
    setWipeResult('');
    try {
      const r = await api.post('/data/wipe-all', { confirm: wipeConfirm });
      setWipeResult(r.data.message);
      setWipeConfirm('');
      load();
    } catch (e) {
      setWipeResult(e.response?.data?.error || 'Could not wipe data');
    } finally {
      setWiping(false);
    }
  }

  return (
    <div style={{ maxWidth: 600 }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Record Counts</h4>
      {counts && (
        <table className="data-table" style={{ marginBottom: 28 }}>
          <tbody>
            {Object.entries(counts).map(([table, count]) => (
              <tr key={table}><td>{TABLE_LABELS[table] || table}</td><td>{count}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Delete a single record</h4>
      <p className="form-hint" style={{ marginBottom: 12 }}>
        Only works if nothing else references it (e.g. a Work Order with a Purchase Order attached can't be deleted until the PO is deleted first).
      </p>
      {deleteResult && (
        <div style={{ background: deleteResult.includes('deleted') ? 'var(--success-bg)' : 'var(--danger-bg)', color: deleteResult.includes('deleted') ? 'var(--success-text)' : 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
          {deleteResult}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        <select value={deleteForm.table} onChange={e => setDeleteForm(f => ({ ...f, table: e.target.value }))} style={{ flex: 1 }}>
          <option value="">Select table…</option>
          {Object.keys(TABLE_LABELS).map(t => <option key={t} value={t}>{TABLE_LABELS[t]}</option>)}
        </select>
        <input placeholder="Record ID" value={deleteForm.id} onChange={e => setDeleteForm(f => ({ ...f, id: e.target.value }))} style={{ width: 100 }} />
        <button className="btn btn-secondary" onClick={handleDelete} disabled={deleting || !deleteForm.table || !deleteForm.id}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>

      <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--danger-text)' }}>Wipe all data</h4>
      <p className="form-hint" style={{ marginBottom: 12 }}>
        Permanently deletes every Client, Vendor, Contractor, Work Order, invoice, and payment. Your user account and login are preserved. This cannot be undone — only use this for clearing test data.
      </p>
      {wipeResult && (
        <div style={{ background: wipeResult.includes('wiped') ? 'var(--success-bg)' : 'var(--danger-bg)', color: wipeResult.includes('wiped') ? 'var(--success-text)' : 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 14 }}>
          {wipeResult}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          placeholder='Type "WIPE ALL DATA" to confirm'
          value={wipeConfirm}
          onChange={e => setWipeConfirm(e.target.value)}
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" style={{ background: 'var(--danger-text)' }} onClick={handleWipe} disabled={wiping || wipeConfirm !== 'WIPE ALL DATA'}>
          {wiping ? 'Wiping…' : 'Wipe All Data'}
        </button>
      </div>
    </div>
  );
}
